<template>
  <div class="p-6 max-w-6xl mx-auto space-y-6">
    <el-card shadow="hover">
      <template #header>
  <div class="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
          <h2 class="text-lg font-semibold">{{ isMobile() ? '退休规划' : '退休规划（两阶段年金）' }}</h2>
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-3" title="用途：根据退休期消费目标与投资回报，反推退休时需积累金额与工作期每年应储蓄金额。" />
      <FormulaPanel
        title="两阶段年金：工作期储蓄→退休期消费"
        :formulas="[needFormula, savingFormula]"
        :variables="retireVars"
        :notes="['全部以年为周期，支付默认在期末']"
      />
  <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <el-form label-position="top" class="md:col-span-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          <el-form-item label="工作期(年)/ n_w"><el-input v-model.number="form.workYears" type="number"/></el-form-item>
          <el-form-item label="退休期(年)/ n_r"><el-input v-model.number="form.retireYears" type="number"/></el-form-item>
          <el-form-item label="年收益率(%) / r"><el-input v-model.number="form.annualRorPct" type="number"/></el-form-item>
          <el-form-item :label="`每年支出（${symbol}）`">
            <el-input v-model.number="retireSpendUI" type="number"/>
            <div class="text-xs text-gray-500 mt-1">（此处填写PMT_r）</div>
          </el-form-item>
          <el-form-item :label="`现有储蓄（${symbol}）`">
            <el-input v-model.number="existingUI" type="number"/>
            <div class="text-xs text-gray-500 mt-1">（此处填写PV_0）</div>
          </el-form-item>
        </el-form>
  <div class="md:col-span-1 flex gap-3 md:justify-end flex-wrap">
          <el-button type="primary" @click="onCalc">计算</el-button>
          <el-button @click="onReset">重置</el-button>
        </div>
      </div>

      <el-alert v-if="error" type="error" class="mt-4" :closable="false" :title="error" />

      <el-collapse-transition>
        <div v-if="result" class="mt-4">
  <el-descriptions title="结果（年为周期）" :column="3" border>
    <el-descriptions-item :label="`退休时所需储蓄 PV（${symbol}）`">{{ d2(result.needAtRetire) }}</el-descriptions-item>
    <el-descriptions-item :label="`工作期每年储蓄 PMT（${symbol}）`">{{ d2(result.savePerYear) }}</el-descriptions-item>
    <el-descriptions-item :label="`工作期累积 FV（${symbol}，含现有）`">{{ d2(result.accumulateFV) }}</el-descriptions-item>
    <el-descriptions-item :label="`缺口/结余（${symbol}）`">{{ d2(result.gap) }}</el-descriptions-item>
        </el-descriptions>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { round, pvAnnuity, fvAnnuity, pmtFromFV } from '@/lib/tvmEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
function isMobile() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
const form = ref({ workYears: 30, retireYears: 25, annualRorPct: 4, retireSpend: 120000, existing: 0 })
const result = ref(null)
const error = ref('')

// 货币换算
const { symbol, toDisplay, toBase } = useCurrency()
const retireSpendUI = computed({ get: () => toDisplay(form.value.retireSpend), set: v => form.value.retireSpend = toBase(v) })
const existingUI = computed({ get: () => toDisplay(form.value.existing), set: v => form.value.existing = toBase(v) })

const onCalc = () => {
  error.value = ''
  result.value = null
  try {
    const { workYears, retireYears, annualRorPct, retireSpend, existing } = form.value
    const r = Number(annualRorPct)/100
    // 1) 退休期消费的现值（退休时刻的需要）
    const needAtRetire = round(pvAnnuity(retireSpend, r, retireYears))
    // 2) 工作期需要积累到 needAtRetire，考虑已有 existing 的未来值
    const existingFV = round(existing * Math.pow(1 + r, workYears))
    const gapFV = Math.max(0, needAtRetire - existingFV)
    // 3) 求工作期每年储蓄额，使得 FV 达到 gapFV
    const savePerYear = round(pmtFromFV(gapFV, r, workYears))
    const accumulateFV = round(existingFV + fvAnnuity(savePerYear, r, workYears))
    const gap = round(accumulateFV - needAtRetire)
    result.value = { needAtRetire, savePerYear, accumulateFV, gap }
  } catch (e) {
    error.value = e?.message || '计算失败'
  }
}

const onReset = () => {
  result.value = null
  error.value = ''
}

// 显示用：金额两位小数
const d2 = (v) => Number(toDisplay(v)).toFixed(2)

// 公式与变量映射（年为周期）
const needFormula = String.raw`PV_{retire} = PMT_r \cdot \dfrac{1 - (1+r)^{-n_r}}{r}`
const savingFormula = String.raw`PMT_w = FV_{gap} \cdot \dfrac{r}{(1+r)^{n_w} - 1}`
const retireVars = [
  { symbol: 'n_w', label: '工作期年数' },
  { symbol: 'n_r', label: '退休期年数' },
  { symbol: 'r', label: '年收益率' },
  { symbol: 'PMT_r', label: '退休期每年支出' },
  { symbol: 'PV_0', label: '当前储蓄' },
  { symbol: 'FV_{gap}', label: '退休时需达到的目标（扣除现有储蓄按年复利后的FV）' }
]

</script>
